import React from "react";
import { getVisiblePageNumbers } from "../lib/pagination";

export interface PaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  className?: string;
  itemLabel?: string; // default: "itens" or "registros"
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  pageSize = 15,
  onPageChange,
  className = "",
  itemLabel = "itens",
}) => {
  // Não renderiza controles de navegação caso haja 15 ou menos itens (ou 0)
  if (totalItems <= pageSize) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalItems);

  const visiblePages = getVisiblePageNumbers(currentPage, totalPages);

  return (
    <nav
      className={`pagination-container ${className}`}
      aria-label="Paginação da tabela"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "var(--space-2)",
        padding: "0.4rem 0.6rem",
        borderTop: "1px solid var(--color-border)",
        background: "var(--color-surface-sunken)",
        borderRadius: "0 0 var(--radius) var(--radius)",
        fontSize: "var(--text-xs)",
      }}
    >
      <div className="pagination-summary text-muted" style={{ fontWeight: 500 }}>
        Mostrando <strong style={{ color: "var(--color-text)" }}>{startIndex}</strong>–
        <strong style={{ color: "var(--color-text)" }}>{endIndex}</strong> de{" "}
        <strong style={{ color: "var(--color-text)" }}>{totalItems}</strong> {itemLabel}
      </div>

      <div
        className="pagination-controls"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
        }}
      >
        <button
          type="button"
          className="btn btn-secondary pagination-btn"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          title="Primeira página"
          style={{
            padding: "2px 7px",
            fontSize: "11px",
            minWidth: "26px",
            height: "26px",
            lineHeight: "22px",
          }}
        >
          «
        </button>

        <button
          type="button"
          className="btn btn-secondary pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          title="Página anterior"
          style={{
            padding: "2px 8px",
            fontSize: "11px",
            minWidth: "26px",
            height: "26px",
            lineHeight: "22px",
          }}
        >
          ‹ Anterior
        </button>

        <div style={{ display: "inline-flex", gap: "2px" }}>
          {visiblePages.map((page, idx) => {
            if (page === "...") {
              return (
                <span
                  key={`ellipsis-${idx}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "24px",
                    height: "26px",
                    color: "var(--color-text-secondary)",
                    fontSize: "11px",
                  }}
                >
                  …
                </span>
              );
            }

            const pageNum = Number(page);
            const isCurrent = pageNum === currentPage;

            return (
              <button
                key={pageNum}
                type="button"
                className={`pagination-page-btn ${isCurrent ? "btn" : "btn btn-secondary"}`}
                onClick={() => onPageChange(pageNum)}
                aria-current={isCurrent ? "page" : undefined}
                style={{
                  padding: "2px 6px",
                  fontSize: "11px",
                  minWidth: "26px",
                  height: "26px",
                  lineHeight: "22px",
                  fontWeight: isCurrent ? 700 : 500,
                  background: isCurrent ? "var(--color-accent)" : undefined,
                  color: isCurrent ? "var(--color-accent-contrast)" : undefined,
                  borderColor: isCurrent ? "var(--color-accent)" : undefined,
                }}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="btn btn-secondary pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          title="Próxima página"
          style={{
            padding: "2px 8px",
            fontSize: "11px",
            minWidth: "26px",
            height: "26px",
            lineHeight: "22px",
          }}
        >
          Próxima ›
        </button>

        <button
          type="button"
          className="btn btn-secondary pagination-btn"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          title="Última página"
          style={{
            padding: "2px 7px",
            fontSize: "11px",
            minWidth: "26px",
            height: "26px",
            lineHeight: "22px",
          }}
        >
          »
        </button>
      </div>
    </nav>
  );
};
